import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Collapse, Divider, Grid, IconButton,
  MenuItem, Paper, Select, Stack, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import TuneIcon from "@mui/icons-material/Tune";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PreviewIcon from "@mui/icons-material/Preview";
import { BORDER, NAVY, TEXT_SECONDARY, TEXT_PRIMARY } from "../../../../theme/tokens";
import { useNotifier } from "../../../../Core/Notifier";
import {
  useGetPosMappingContextQuery, useMapPosCategoryMutation, useUpdatePosMappingMutation,
  useReorderPosCategoriesMutation, useDeletePosMappingMutation, useGetPosItemsQuery,
  useUpsertPosItemOverrideMutation,
} from "../../../../Api/attoDeskApi";
import MenuBoardPreviewDialog from "./MenuBoardPreviewDialog";

const POS_TEAL = "#00838f";

const teal = { textTransform: "none", fontWeight: 700, bgcolor: POS_TEAL, "&:hover": { bgcolor: "#006974" } } as const;

/** Menu Mapping — assign Clover categories to menu_board zones and style them. */
const MenuMappingPanel: React.FC<{ connectionId: number; playlistId?: number }> = ({ connectionId, playlistId }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [targetZoneId, setTargetZoneId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: ctxResp, isFetching } = useGetPosMappingContextQuery({
    id: connectionId, template_id: templateId || undefined,
  });
  const ctx = ctxResp?.data as any;
  const templates: any[] = ctx?.templates ?? [];
  const categories: any[] = ctx?.categories ?? [];
  const zones: any[] = ctx?.zones ?? [];
  const mappings: any[] = ctx?.mappings ?? [];

  // Seed template + target zone once data arrives.
  useEffect(() => {
    if (templateId == null && templates.length) setTemplateId(templates[0].id);
  }, [templates, templateId]);
  useEffect(() => {
    if (targetZoneId == null && zones.length) setTargetZoneId(zones[0].id);
  }, [zones, targetZoneId]);

  const [mapCategory, { isLoading: mapping }] = useMapPosCategoryMutation();
  const [deleteMapping] = useDeletePosMappingMutation();

  const doMap = async (posCategoryId: number) => {
    if (!templateId || !targetZoneId) { showErrorMessage("Pick a target zone first."); return; }
    try {
      await mapCategory({ id: connectionId, template_id: templateId, zone_id: targetZoneId,
        pos_category_id: posCategoryId, playlist_id: playlistId }).unwrap();
      showMessage("Category mapped");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Could not map category."); }
  };

  const zonesByKey = useMemo(() => {
    const m: Record<string, any> = {};
    zones.forEach((z) => { if (z.zone_key) m[z.zone_key] = z; });
    return m;
  }, [zones]);

  const mappingsByZone = useMemo(() => {
    const m: Record<string, any[]> = {};
    mappings.forEach((mp) => { (m[mp.zone_key] ||= []).push(mp); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.sort_order - b.sort_order));
    return m;
  }, [mappings]);

  const filteredCategories = categories.filter((c) =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()));

  if (isFetching && !ctx) {
    return <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={22} sx={{ color: POS_TEAL }} /></Box>;
  }

  if (templates.length === 0) {
    return (
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 3, textAlign: "center" }}>
        <Typography sx={{ fontWeight: 800, color: NAVY, mb: 0.5 }}>No templates yet</Typography>
        <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
          Create a template with a <b>menu board</b> zone in the Template designer, then map
          your Clover categories onto it here.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Template + preview bar */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>Template</Typography>
        <Select size="small" value={templateId ?? ""} onChange={(e) => { setTemplateId(Number(e.target.value)); setTargetZoneId(null); }}
          sx={{ minWidth: 220 }}>
          {templates.map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.name} · {t.orientation}</MenuItem>
          ))}
        </Select>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<PreviewIcon />} variant="contained" sx={teal}
          disabled={!templateId} onClick={() => setPreviewOpen(true)}>
          Preview
        </Button>
      </Stack>

      {zones.length === 0 ? (
        <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 3, textAlign: "center" }}>
          <Typography sx={{ fontWeight: 800, color: NAVY, mb: 0.5 }}>This template has no menu-board zones</Typography>
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
            Add a <b>menu board</b> widget zone to this template in the designer, then return to map categories onto it.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {/* Left — categories */}
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 1.5, height: "100%" }}>
              <Typography sx={{ fontWeight: 800, color: NAVY, mb: 1 }}>Clover categories</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField size="small" placeholder="Search…" value={search}
                  onChange={(e) => setSearch(e.target.value)} fullWidth />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, whiteSpace: "nowrap" }}>Add to zone</Typography>
                <Select size="small" value={targetZoneId ?? ""} onChange={(e) => setTargetZoneId(Number(e.target.value))} fullWidth>
                  {zones.map((z, i) => (
                    <MenuItem key={z.id} value={z.id}>{z.name || `Menu zone ${i + 1}`}</MenuItem>
                  ))}
                </Select>
              </Stack>
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ maxHeight: 420, overflow: "auto" }}>
                {filteredCategories.map((c) => (
                  <Box key={c.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                    py: 0.5, borderBottom: `1px solid ${BORDER}` }}>
                    <Box>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: TEXT_PRIMARY }}>{c.name}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY }}>{c.items_count} items</Typography>
                    </Box>
                    <Button size="small" startIcon={<AddIcon />} disabled={mapping} onClick={() => doMap(c.id)}
                      sx={{ textTransform: "none", fontWeight: 700, color: POS_TEAL }}>Map</Button>
                  </Box>
                ))}
                {filteredCategories.length === 0 && (
                  <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, py: 2, textAlign: "center" }}>No categories.</Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Right — zones + mappings */}
          <Grid item xs={12} md={7}>
            <Stack spacing={1.5}>
              {zones.map((z, zi) => {
                const zoneMappings = z.zone_key ? (mappingsByZone[z.zone_key] ?? []) : [];
                return (
                  <Paper key={z.id} elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Typography sx={{ fontWeight: 800, color: NAVY }}>{z.name || `Menu zone ${zi + 1}`}</Typography>
                      <Chip size="small" label={`${Math.round(z.w)}×${Math.round(z.h)}%`} sx={{ fontWeight: 700, bgcolor: "#EEF2F6", color: TEXT_SECONDARY }} />
                    </Box>
                    {zoneMappings.length === 0 ? (
                      <Typography sx={{ fontSize: 12.5, color: TEXT_SECONDARY }}>
                        No categories mapped. Pick this zone on the left and press <b>Map</b>.
                      </Typography>
                    ) : (
                      zoneMappings.map((mp, mi) => (
                        <MappedCategory key={mp.id} mp={mp} first={mi === 0} last={mi === zoneMappings.length - 1}
                          connectionId={connectionId} templateId={templateId!} zoneKey={z.zone_key}
                          orderedIds={zoneMappings.map((x) => x.id)}
                          onRemove={async () => {
                            try { await deleteMapping(mp.id).unwrap(); showMessage("Mapping removed"); }
                            catch (e: any) { showErrorMessage(e?.data?.message || "Could not remove."); }
                          }} />
                      ))
                    )}
                  </Paper>
                );
              })}
            </Stack>
          </Grid>
        </Grid>
      )}

      {templateId && (
        <MenuBoardPreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)}
          connectionId={connectionId} templateId={templateId} />
      )}
    </Box>
  );
};

/* ---- one mapped category with appearance + item overrides ---- */

const SEL = { minWidth: 118 } as const;

const MappedCategory: React.FC<{
  mp: any; first: boolean; last: boolean; connectionId: number; templateId: number;
  zoneKey: string; orderedIds: number[]; onRemove: () => void;
}> = ({ mp, first, last, connectionId, templateId, zoneKey, orderedIds, onRemove }) => {
  const { showErrorMessage } = useNotifier();
  const [update] = useUpdatePosMappingMutation();
  const [reorder] = useReorderPosCategoriesMutation();
  const [showSettings, setShowSettings] = useState(false);
  const [showItems, setShowItems] = useState(false);
  const a = mp.appearance || {};

  const setAppearance = async (patch: Record<string, any>) => {
    try { await update({ mappingId: mp.id, body: { appearance: patch } }).unwrap(); }
    catch (e: any) { showErrorMessage(e?.data?.message || "Could not update layout."); }
  };
  const move = async (dir: -1 | 1) => {
    const ids = [...orderedIds];
    const i = ids.indexOf(mp.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try { await reorder({ id: connectionId, template_id: templateId, zone_key: zoneKey, mapping_ids: ids }).unwrap(); }
    catch (e: any) { showErrorMessage("Could not reorder."); }
  };

  return (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 1, mb: 0.75 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: TEXT_PRIMARY, flex: 1 }}>{mp.category_name}</Typography>
        <Tooltip title="Move up"><span><IconButton size="small" disabled={first} onClick={() => move(-1)}><ArrowUpwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
        <Tooltip title="Move down"><span><IconButton size="small" disabled={last} onClick={() => move(1)}><ArrowDownwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
        <Tooltip title="Layout"><IconButton size="small" onClick={() => setShowSettings((s) => !s)}><TuneIcon fontSize="inherit" /></IconButton></Tooltip>
        <Tooltip title="Item overrides"><IconButton size="small" onClick={() => setShowItems((s) => !s)}><VisibilityIcon fontSize="inherit" /></IconButton></Tooltip>
        <Tooltip title="Remove"><IconButton size="small" onClick={onRemove}><DeleteOutlineIcon fontSize="inherit" /></IconButton></Tooltip>
      </Box>

      <Collapse in={showSettings}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          <TextField size="small" type="number" label="Columns" sx={{ width: 90 }}
            value={a.columns ?? 1} onChange={(e) => setAppearance({ columns: Math.max(1, Number(e.target.value)) })} />
          <LabeledSelect label="Item align" value={a.item_alignment ?? "left"}
            opts={["left", "center", "right"]} onChange={(v) => setAppearance({ item_alignment: v })} />
          <LabeledSelect label="Price align" value={a.price_alignment ?? "right"}
            opts={["inline", "right", "next_line"]} onChange={(v) => setAppearance({ price_alignment: v })} />
          <LabeledSelect label="Image" value={a.image_position ?? "left"}
            opts={["left", "right", "top", "none"]} onChange={(v) => setAppearance({ image_position: v })} />
          <LabeledSelect label="Overflow" value={a.overflow_behavior ?? "shrink"}
            opts={["clip", "shrink", "paginate"]} onChange={(v) => setAppearance({ overflow_behavior: v })} />
          <TextField size="small" type="number" label="Name lines" sx={{ width: 100 }}
            value={a.max_name_lines ?? 1} onChange={(e) => setAppearance({ max_name_lines: Math.max(1, Number(e.target.value)) })} />
          <TextField size="small" type="number" label="Desc lines" sx={{ width: 100 }}
            value={a.max_description_lines ?? 2} onChange={(e) => setAppearance({ max_description_lines: Math.max(0, Number(e.target.value)) })} />
          <ToggleRow label="Image" checked={a.show_image ?? true} onChange={(v) => setAppearance({ show_image: v })} />
          <ToggleRow label="Description" checked={a.show_description ?? true} onChange={(v) => setAppearance({ show_description: v })} />
          <ToggleRow label="Currency $" checked={a.show_currency_symbol ?? true} onChange={(v) => setAppearance({ show_currency_symbol: v })} />
        </Stack>
      </Collapse>

      <Collapse in={showItems}>
        <ItemOverrides connectionId={connectionId} mappingId={mp.id} posCategoryId={mp.pos_category_id}
          overrides={mp.item_overrides ?? []} />
      </Collapse>
    </Box>
  );
};

const LabeledSelect: React.FC<{ label: string; value: string; opts: string[]; onChange: (v: string) => void }> =
  ({ label, value, opts, onChange }) => (
    <Box>
      <Typography sx={{ fontSize: 10, color: TEXT_SECONDARY, mb: 0.2 }}>{label}</Typography>
      <Select size="small" value={value} onChange={(e) => onChange(e.target.value)} sx={SEL}>
        {opts.map((o) => <MenuItem key={o} value={o} sx={{ textTransform: "capitalize" }}>{o.replace(/_/g, " ")}</MenuItem>)}
      </Select>
    </Box>
  );

const ToggleRow: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> =
  ({ label, checked, onChange }) => (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>{label}</Typography>
    </Box>
  );

const ItemOverrides: React.FC<{ connectionId: number; mappingId: number; posCategoryId: number; overrides: any[] }> =
  ({ connectionId, mappingId, posCategoryId, overrides }) => {
    const { showErrorMessage } = useNotifier();
    const { data, isFetching } = useGetPosItemsQuery({ id: connectionId, pos_category_id: posCategoryId });
    const items: any[] = (data?.data as any[]) ?? [];
    const [upsert] = useUpsertPosItemOverrideMutation();
    const ovById = useMemo(() => {
      const m: Record<number, any> = {};
      overrides.forEach((o) => { m[o.pos_item_id] = o; });
      return m;
    }, [overrides]);

    const save = async (posItemId: number, patch: Record<string, any>) => {
      try { await upsert({ mappingId, body: { pos_item_id: posItemId, ...patch } }).unwrap(); }
      catch (e: any) { showErrorMessage(e?.data?.message || "Could not save override."); }
    };

    if (isFetching) return <Box sx={{ py: 1 }}><CircularProgress size={16} sx={{ color: POS_TEAL }} /></Box>;
    return (
      <Box sx={{ mt: 1 }}>
        {items.map((it) => {
          const ov = ovById[it.id] || {};
          return (
            <Box key={it.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.4, borderTop: `1px solid ${BORDER}` }}>
              <Switch size="small" checked={ov.visible ?? true} onChange={(e) => save(it.id, { visible: e.target.checked })} />
              <Typography sx={{ fontSize: 12.5, flex: 1, minWidth: 0, textDecoration: (ov.visible ?? true) ? "none" : "line-through" }} noWrap>
                {ov.name_override || it.name}
              </Typography>
              <TextField size="small" placeholder="Badge" defaultValue={ov.badge_text || ""} sx={{ width: 90 }}
                onBlur={(e) => e.target.value !== (ov.badge_text || "") && save(it.id, { badge_text: e.target.value || null })} />
              <Tooltip title="Highlight">
                <Switch size="small" checked={ov.highlighted ?? false} onChange={(e) => save(it.id, { highlighted: e.target.checked })} />
              </Tooltip>
            </Box>
          );
        })}
        {items.length === 0 && <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, py: 1 }}>No items in this category.</Typography>}
      </Box>
    );
  };

export default MenuMappingPanel;
